import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  effect,
  inject,
  input,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { map } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { IconUIComponent } from '../../atoms/icon/icon.component';
import { FiltersButtonUIComponent } from '../../atoms/filters-button/filters-button.component';
import { SmartFilterUIComponent } from '../../molecules/smart-filter/smart-filter/smart-filter.component';
import { FiltersDialogUIComponent } from '../../molecules/filters-dialog/filters-dialog.component';
import { PageHeaderActions } from '../../atoms/page-header/page-header-actions';
import {
  FilterDefContext,
  FilterDefUIDirective,
  ListFiltersQuickUIDirective,
  ListFiltersSearchUIDirective,
  ListFiltersSettingsUIDirective,
  ListFiltersSmartLabelUIDirective,
  ListFiltersSmartUIDirective,
} from './list-filters.directives';

/** Matches the `filters-compact` Tailwind variant in `tailwind.css`. */
const COMPACT_QUERY = '(max-width: 1024px)';

const DIALOG_CONFIG = {
  width: '100dvw',
  height: '100dvh',
  maxWidth: '100dvw',
  maxHeight: '100dvh',
  panelClass: ['mat-dialog-common', 'full-screen-dialog-container'],
  // A focused first field would pop the on-screen keyboard over the dialog as it opens.
  autoFocus: false,
  closeOnNavigation: true,
};

/**
 * Everything above a list or board that narrows it: search, Smart Filter fields, per-column
 * filters and table settings, with the result count and «Clear filters».
 *
 * A page declares each part once as a template (`fr8botUiListFiltersSearch`, `…Smart`,
 * `…SmartLabel`, `…Settings`, `fr8botUiFilterDef` per column). Above 1024px they render as the
 * Smart Filter toolbar, and the column filters in the table's filter row through
 * `filterTemplate()`; from 1024px down the search stays on the page and a «Filters» button opens
 * the rest in the full-screen `fr8bot-ui-filters-dialog`. Inside a `fr8bot-ui-page-template`
 * with a header that button moves up into the header (`PageHeaderActions`), icon-only on a phone.
 */
@Component({
  selector: 'fr8bot-ui-list-filters',
  templateUrl: './list-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    IconUIComponent,
    FiltersButtonUIComponent,
    SmartFilterUIComponent,
    FiltersDialogUIComponent,
  ],
  // Nothing left on a compact screen (no search, the button in the page header): no empty box
  // taking the parent's gap.
  host: { class: 'tw:block tw:filters-compact:has-[>.list-filters-compact:empty]:hidden' },
})
export class ListFiltersUIComponent {
  readonly filtersCount = input(0);
  /** Rows the current filters return; `null` hides the count. */
  readonly resultsCount = input<number | null>(null);
  /** The table's visible columns: which column filters the dialog shows, and in what order. */
  readonly columns = input<readonly string[]>([]);

  readonly clearFilters = output();

  protected readonly settings = contentChild(ListFiltersSettingsUIDirective);
  protected readonly search = contentChild(ListFiltersSearchUIDirective);
  protected readonly quick = contentChild(ListFiltersQuickUIDirective);
  protected readonly smart = contentChild(ListFiltersSmartUIDirective);
  protected readonly smartLabel = contentChild(ListFiltersSmartLabelUIDirective);
  private readonly filterDefs = contentChildren(FilterDefUIDirective);

  private readonly smartFilter = viewChild(SmartFilterUIComponent);
  private readonly dialogTemplate = viewChild.required<TemplateRef<unknown>>('dialog');
  private readonly headerButtonTemplate = viewChild.required<TemplateRef<unknown>>('headerButton');
  private readonly headerActions = inject(PageHeaderActions, { optional: true });

  private readonly dialog = inject(MatDialog);
  private dialogRef: MatDialogRef<unknown> | null = null;

  private readonly isCompact = toSignal(
    inject(BreakpointObserver)
      .observe(COMPACT_QUERY)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  /**
   * Whether the table shows its filter row: the desktop Smart Filter is open. Never from 1024px
   * down, where the column filters are in the dialog instead.
   */
  readonly isOpen = computed(() => !this.isCompact() && (this.smartFilter()?.isOpen() ?? false));

  /** Every column with a filter, visible or not — the table declares a filter cell for each. */
  readonly filterColumns = computed(() => this.filterDefs().map((def) => def.key()));

  /** The column filters the dialog lists: the visible columns that have one, in their order. */
  readonly dialogFilters = computed(() => {
    const byKey = new Map(this.filterDefs().map((def) => [def.key(), def]));

    return this.columns().flatMap((column) => byKey.get(column) ?? []);
  });

  /** Without Smart Filter fields or column filters the toolbar has nothing to open. */
  protected readonly hasToggle = computed(() => !!this.smart() || this.filterDefs().length > 0);

  /** The search lives outside the dialog, so a page with nothing else gets no «Filters» button. */
  protected readonly hasDialog = computed(
    () => !!this.settings() || !!this.quick() || this.hasToggle(),
  );

  /** On compact screens the «Filters» button sits in the page header, next to the view toggle. */
  protected readonly buttonInHeader = computed(
    () => this.isCompact() && this.hasDialog() && !!this.headerActions?.hasHeader(),
  );

  constructor() {
    effect(() => {
      if (!this.isCompact()) {
        this.closeDialog();
      }
    });

    effect((onCleanup) => {
      const actions = this.headerActions;
      const template = this.headerButtonTemplate();

      if (actions && this.buttonInHeader()) {
        actions.show(template);
        onCleanup(() => actions.hide(template));
      }
    });
  }

  filterTemplate(column: string): TemplateRef<FilterDefContext> | null {
    return this.filterDefs().find((def) => def.key() === column)?.template ?? null;
  }

  protected openDialog(): void {
    this.dialogRef = this.dialog.open(this.dialogTemplate(), DIALOG_CONFIG);
  }

  protected closeDialog(): void {
    if (this.dialogRef?.getState() === MatDialogState.OPEN) {
      this.dialogRef.close();
    }
  }
}
